import {Inject, Injectable, Injector, Type} from "@angular/core";
import {ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment} from "@angular/router";

import {AppService} from "app/app.service";
import {APP_DOMAIN_CONTEXT_TOKEN} from "app/services/domain/app-domain-context.token";
import type {AppDomainContext} from "app/services/domain/domain.module";
import * as Models from "app/services/proxy/model/models";
import {PolicyUser, TenantPolicyUser} from "app/shared/permissions/policy-user";
import {ApiClient} from "framework/services/api.client";
import {ErrorService} from "framework/services/error.service";
import {BaseComponent} from "framework/ui/components";


@Injectable()
export class AuthGuard
{
    private appService: AppService;

    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext,
                private apiClient: ApiClient,
                inj: Injector)
    {
        this.appService = inj.get(AppService);
    }

    canActivateChild(route: ActivatedRouteSnapshot,
                     state: RouterStateSnapshot): Promise<boolean>
    {
        return this.canProceed(route, state.url);
    }

    public async canLoad(route: Route,
                         segments: UrlSegment[])
    {
        if (!this.domain.users.isInitialized)
        {
            try
            {
                await this.domain.users.init();

                // Initialize LaunchDarkly before any route resolver runs.
                if (this.domain.users.isAuthenticated)
                {
                    this.domain.featureFlagsRequester.initializeProviderFromCurrentUser();
                }

                // if user is logged in, allow the navigation
                return true;
            }
            catch (e)
            {
            }
        }

        if (segments.length > 0 && segments[0].path === "start")
        {
            return true;
        }

        if (this.domain.users.isAuthenticated)
        {
            return true;
        }
        else
        {
            // Use the full window.location URL (pathname + search) so query params such as
            // ?view=<id> are preserved through the login redirect for fresh unauthenticated users.
            // UrlSegment[] does not carry query params, so segments.join() would drop them.
            this.queueNavigationToLogin(window.location.pathname + window.location.search);
            return false;
        }
    }

    private async canProceed(route: ActivatedRouteSnapshot,
                             url: string): Promise<boolean>
    {
        try
        {
            let nextAction = await this.computeNextAction(route, url);
            switch (nextAction)
            {
                case NextAction.ProceedAndResetState:
                    this.appService.setState("startPage", false);
                    this.appService.setState("pageFullscreen", false);
                    return true;

                case NextAction.Proceed:
                    return true;

                default:
                    this.queueNavigationToLogin(url);
                    return false;
            }
        }
        catch (e)
        {
            this.queueNavigationToLogin(url);
            return false;
        }
    }

    private queueNavigationToLogin(url: string)
    {
        this.apiClient.configuration.redirectToLogin(url);
    }

    private async computeNextAction(route: ActivatedRouteSnapshot,
                                    url: string): Promise<NextAction>
    {
        let routeLeaf = route;
        while (true)
        {
            if (routeLeaf.children && routeLeaf.children.length == 1)
            {
                routeLeaf = routeLeaf.children[0];
                continue;
            }

            break;
        }

        if (!this.domain.users.isAuthenticated)
        {
            let cfg = AuthorizationContext.lookup(routeLeaf.component);
            if (cfg && cfg.noAuth)
            {
                // It was a request to a page that doesn't require authentication, let it go.
                this.appService.setState("startPage", false);
                return NextAction.Proceed;
            }

            return NextAction.GoToLogin;
        }

        // if they are authenticated, allow the navigation
        // disable start page / fullscreen settings that are set by default
        return NextAction.ProceedAndResetState;
    }
}

@Injectable({providedIn: "root"})
export class LoginGuard
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext,
                private appService: AppService,
                private router: Router)
    {
    }

    public async canLoad(): Promise<boolean>
    {
        if (this.domain.users.isAuthenticated)
        {
            this.appService.setState("hideStandardLayout", true);
            this.router.navigate(["provider/services/master-account-selection"]);
            return false;
        }
        return true;
    }
}

@Injectable({providedIn: "root"})
export class ProductAdminAuthGuard
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext)
    {
    }

    public async canLoad(): Promise<boolean>
    {
        return this.domain.users.isProductAdmin;
    }
}

@Injectable({providedIn: "root"})
export class MaintenancePermissionAuthGuard extends TenantPolicyUser
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext)
    {
        super(domain.users.userDetails, domain.users.activeTenantId);
    }

    public async canLoad()
    {
        if (this.domain.users.isProductAdmin)
        {
            return true;
        }
        return this.canRead;
    }

    policyDomains(): Models.PolicyEnum[]
    {
        return [Models.PolicyEnum.maintenance];
    }

    get canRead(): boolean
    {
        return this.has(Models.ActionEnum.read);
    }
}

@Injectable({providedIn: "root"})
export class ReadPermissionAuthGuard
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext,
                private router: Router)
    {}

    public async canLoad(userClass: Type<PolicyUser>): Promise<boolean>
    {
        if (!this.domain.users.isAuthenticated) return false;

        const userPolicy = new userClass(this.domain.users.userDetails, this.domain.users.lastActiveTenantId);
        if (userPolicy.canRead)
        {
            return true;
        }
        else
        {
            this.router.navigate(["provider/services/account-governance/cloud-accounts"]);
            return false;
        }
    }
}

@Injectable({providedIn: "root"})
export class TokenOpsEnabledGuard
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext,
                private router: Router,
                private errors: ErrorService)
    {}

    public async canMatch(): Promise<boolean>
    {
        const details = await this.domain.users.getMasterAccount();
        if (details?.tokenops_enabled) return true;

        this.errors.warn("Please contact your admin to enable this feature.", -1);
        this.router.navigate(["provider/services/executivedashboard/finops"]);
        return false;
    }
}

@Injectable({providedIn: "root"})
export class ActionPermissionAuthGuard
{
    constructor(@Inject(APP_DOMAIN_CONTEXT_TOKEN) private domain: AppDomainContext,
                private router: Router)
    {}

    public async canLoad<T extends TenantPolicyUser>(userClass: Type<T>,
                                                     getPermission: (user: T) => boolean): Promise<boolean>
    {
        if (!this.domain.users.isAuthenticated) return false;

        const userPolicy = new userClass(this.domain.users.userDetails, this.domain.users.lastActiveTenantId);
        if (getPermission(userPolicy))
        {
            return true;
        }
        else
        {
            this.router.navigate(["provider/services/account-governance/cloud-accounts"]);
            return false;
        }
    }
}

export interface AuthorizationDefinition<T extends BaseComponent | void> extends Type<T>
{
}

export interface AuthorizationConfig
{
    noAuth?: boolean;
}

export abstract class AuthorizationContext
{
    private static s_modelPrototypeToDescriptor: Map<Type<any>, AuthorizationConfig> = new Map();

    static AuthorizationDef<S extends BaseComponent, T extends AuthorizationDefinition<S>>(config: AuthorizationConfig): any
    {
        return function (definition: T)
        {
            AuthorizationContext.s_modelPrototypeToDescriptor.set(definition, config);

            // Link the descriptor to the block's prototype.
            let blockDef                    = definition.prototype;
            blockDef.$$AuthorizationContext = config;

            return definition;
        };
    }

    static lookup(comp: Type<any> | string | null): AuthorizationConfig
    {
        if (!comp || typeof comp == "string") return null;

        return AuthorizationContext.s_modelPrototypeToDescriptor.get(comp);
    }
}

export const AuthorizationDef = AuthorizationContext.AuthorizationDef;

enum NextAction
{
    Proceed, ProceedAndResetState, GoToLogin
}
